/**
 * Before Entry Page - 入室前画面
 * Display streamer info and entry buttons before entering a room
 *
 * Public access: This page is accessible without authentication
 * Room entry requires authentication
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { usePoints } from '../hooks/usePoints';
import { apiClient } from '../lib/api-client';
import { usePartyCount } from '../hooks/usePartyCount';
import { ROUTES } from '../lib/constants';
import { resolveMediaUrl } from '../lib/media-url';

import ViewerHeader from '../components/ViewerHeader';
import PointsPurchaseModal from '../components/PointsPurchaseModal';
import MyPageModal from '../components/MyPageModal';
import FavoritesModal from '../components/FavoritesModal';
import NotificationsModal from '../components/NotificationsModal';
import BeforeEntryView from '../components/Watch/BeforeEntryView';

export default function BeforeEntry() {
    const { t } = useTranslation('watch');
    const { streamerId } = useParams<{ streamerId: string }>();
    const [searchParams] = useSearchParams();
    const scheduleId = searchParams.get('schedule');
    const navigate = useNavigate();
    const { user, logout, isAdminOrManager } = useAuth();
    const { points, purchasePoints } = usePoints();

    // Check if user is authenticated
    const isAuthenticated = !!user;

    // Handler for login button
    const handleLoginClick = useCallback(() => {
        navigate(ROUTES.LOGIN);
    }, [navigate]);

    // Handler for register button
    const handleRegisterClick = useCallback(() => {
        navigate(ROUTES.REGISTER);
    }, [navigate]);

    // State
    const [streamKey, setStreamKey] = useState<string | null>(null);
    const [streamerName, setStreamerName] = useState<string>(t('list.defaultStreamer'));
    const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
    const [streamerDbId, setStreamerDbId] = useState<number | null>(null);
    const [isOnline, setIsOnline] = useState(false);
    const [isSuspended, setIsSuspended] = useState(false);
    const [nextScheduledEventId, setNextScheduledEventId] = useState<string | null>(null);

    // Modals
    const [isPointsModalOpen, setIsPointsModalOpen] = useState(false);
    const [isMyPageModalOpen, setIsMyPageModalOpen] = useState(false);
    const [isFavoritesModalOpen, setIsFavoritesModalOpen] = useState(false);
    const [isNotificationsModalOpen, setIsNotificationsModalOpen] = useState(false);
    const [handleName, setHandleName] = useState<string | null>(null);
    const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);

    // Polling ref
    const beforeEntryPollingRef = useRef<number | null>(null);

    // Party/Peek counts (for status bar) - works without authentication
    const { partyCount, peekCount } = usePartyCount(streamKey || '');

    // 1. Fetch Streamer Profile (once)
    useEffect(() => {
        if (!streamerId) return;

        const fetchProfile = async () => {
            try {
                const profile = await apiClient.getStreamerProfile(streamerId);
                setStreamerName(profile.display_name || profile.username || streamerId);
                setStreamerDbId(profile.user_id);
                setProfileImageUrl(
                    profile.profile_image_url ? resolveMediaUrl(profile.profile_image_url) : null
                );
            } catch (e) {
                console.error('Failed to fetch profile', e);
                setStreamerName(streamerId);
            }
        };

        fetchProfile();
    }, [streamerId]);

    // 1.0.1. Fetch Viewer's handle_name
    useEffect(() => {
        if (!user) return;

        const fetchViewerProfile = async () => {
            try {
                const profile = await apiClient.getMyProfile();
                setHandleName(profile.handle_name);
            } catch (e) {
                console.error('Failed to fetch viewer profile', e);
            }
        };

        fetchViewerProfile();
    }, [user]);

    // Fetch unread notification count
    const fetchUnreadNotificationCount = useCallback(async () => {
        if (!user) {
            setUnreadNotificationCount(0);
            return;
        }
        try {
            const response = await apiClient.getUnreadNotificationCount();
            setUnreadNotificationCount(response.unread_count);
        } catch (e) {
            console.error('Failed to fetch unread notification count', e);
        }
    }, [user]);

    useEffect(() => {
        fetchUnreadNotificationCount();
    }, [fetchUnreadNotificationCount]);

    // Fetch next scheduled event for this streamer (for event notification feature)
    useEffect(() => {
        if (!streamerDbId) return;

        const fetchNextScheduledEvent = async () => {
            try {
                const schedulesResponse = await apiClient.listEventSchedules({
                    streamer_id: streamerDbId,
                });
                // Find the next scheduled event (status = 'scheduled')
                const scheduledEvents = schedulesResponse.schedules
                    .filter(s => s.status === 'scheduled')
                    .sort((a, b) => new Date(a.scheduled_start).getTime() - new Date(b.scheduled_start).getTime());

                if (scheduledEvents.length > 0) {
                    setNextScheduledEventId(scheduledEvents[0].id);
                }
            } catch (e) {
                console.error('Failed to fetch scheduled events:', e);
            }
        };

        fetchNextScheduledEvent();
    }, [streamerDbId]);

    // Handle notifications modal close - refresh unread count since modal marks them as read
    const handleNotificationsModalClose = useCallback(() => {
        setIsNotificationsModalOpen(false);
        fetchUnreadNotificationCount();
    }, [fetchUnreadNotificationCount]);

    // 1.1. Poll stream status
    // Use schedules (DB) as the source of truth for status, then get stream_key from active_streams
    useEffect(() => {
        if (!streamerId) return;

        const checkStreamStatus = async () => {
            try {
                // Step 1: Check schedule status from DB (source of truth)
                // This ensures consistency with Console and Watch List
                let scheduleStatus: string | null = null;
                let foundScheduleId: string | null = scheduleId;

                if (scheduleId) {
                    // If schedule ID is specified, get that specific schedule
                    const schedulesResponse = await apiClient.listEventSchedules({});
                    const schedule = schedulesResponse.schedules.find(s => s.id === scheduleId);
                    scheduleStatus = schedule?.status || null;
                } else if (streamerDbId) {
                    // Find any live/suspended schedule for this streamer
                    const schedulesResponse = await apiClient.listEventSchedules({
                        streamer_id: streamerDbId,
                    });
                    const liveSchedule = schedulesResponse.schedules.find(
                        s => s.status === 'live' || s.status === 'suspended'
                    );
                    scheduleStatus = liveSchedule?.status || null;
                    foundScheduleId = liveSchedule?.id || null;
                }

                // Step 2: If schedule says live/suspended, get stream_key from active_streams
                if (scheduleStatus === 'live' || scheduleStatus === 'suspended') {
                    const streams = await apiClient.getActiveStreams();
                    const stream = foundScheduleId
                        ? streams.streams.find(s => s.schedule_id === foundScheduleId)
                        : streams.streams.find(s => s.username === streamerId);

                    if (scheduleStatus === 'live') {
                        setIsOnline(true);
                        setIsSuspended(false);
                        setStreamKey(stream?.stream_key || null);
                    } else if (scheduleStatus === 'suspended') {
                        setIsOnline(false);
                        setIsSuspended(true);
                        setStreamKey(stream?.stream_key || null);
                    }
                } else {
                    // Schedule not live/suspended
                    setIsOnline(false);
                    setIsSuspended(false);
                    setStreamKey(null);
                }
            } catch (e) {
                console.error('Failed to fetch stream status', e);
            }
        };

        // Initial check
        checkStreamStatus();

        // Poll every 3 seconds (reduced from 5s for faster stream status detection)
        beforeEntryPollingRef.current = window.setInterval(checkStreamStatus, 3000);

        return () => {
            if (beforeEntryPollingRef.current) {
                clearInterval(beforeEntryPollingRef.current);
                beforeEntryPollingRef.current = null;
            }
        };
    }, [streamerId, scheduleId, streamerDbId]);

    // 2. Handle State Transitions
    const handleEnterLiveRoom = () => {
        // Require authentication to enter room
        if (!isAuthenticated) {
            navigate(ROUTES.LOGIN);
            return;
        }
        if (!isOnline || !streamKey) return;
        navigate(ROUTES.WATCH_LIVE_ROOM(streamerId!));
    };

    const handleEnterPeekRoom = () => {
        // Require authentication to enter room
        if (!isAuthenticated) {
            navigate(ROUTES.LOGIN);
            return;
        }
        if (!isOnline || !streamKey) return;
        navigate(ROUTES.WATCH_PEEK_ROOM(streamerId!));
    };

    const handleExit = () => {
        if (isAdminOrManager()) {
            navigate(ROUTES.CONSOLE);
        } else {
            navigate(ROUTES.WATCH);
        }
    };

    const handlePurchasePoints = async (amount: 1000 | 3000 | 5000 | 10000 | 30000 | 50000) => {
        await purchasePoints(amount);
    };

    return (
        <div className="min-h-screen bg-gray-200">
            <ViewerHeader
                points={isAuthenticated ? points : null}
                onPointsClick={() => setIsPointsModalOpen(true)}
                onExitClick={handleExit}
                showExitButton={true}
                exitButtonLabel={isAdminOrManager() ? '管理画面に戻る' : t('header.exitToList')}
                title={t('header.beforeEntry')}
                icon="🚪"
                username={user?.username}
                onLogout={logout}
                onMyPageClick={() => setIsMyPageModalOpen(true)}
                onFavoritesClick={() => setIsFavoritesModalOpen(true)}
                onNotificationsClick={() => setIsNotificationsModalOpen(true)}
                notificationCount={unreadNotificationCount}
                isAuthenticated={isAuthenticated}
                onLoginClick={handleLoginClick}
                onRegisterClick={handleRegisterClick}
                showLanguageSelector={true}
                isAdminOrManager={isAdminOrManager()}
            />

            <div className="max-w-[1600px] mx-auto px-4 py-4">
                <BeforeEntryView
                    streamerName={streamerName}
                    profileImageUrl={profileImageUrl}
                    isOnline={isOnline}
                    isSuspended={isSuspended}
                    streamKey={streamKey}
                    streamerId={streamerId || ''}
                    streamerDbId={streamerDbId}
                    scheduleId={scheduleId || nextScheduledEventId}
                    onEnterLiveRoom={handleEnterLiveRoom}
                    onEnterPeekRoom={handleEnterPeekRoom}
                    username={user?.username || ''}
                    handleName={handleName}
                    onHandleNameUpdate={(newName) => setHandleName(newName)}
                    isAuthenticated={isAuthenticated}
                    partyCount={partyCount}
                    peekCount={peekCount}
                />
            </div>

            <PointsPurchaseModal
                isOpen={isPointsModalOpen}
                onClose={() => setIsPointsModalOpen(false)}
                currentPoints={points}
                onPurchase={handlePurchasePoints}
            />

            <MyPageModal
                isOpen={isMyPageModalOpen}
                onClose={() => setIsMyPageModalOpen(false)}
                points={points}
                username={user?.username || ''}
                handleName={handleName}
                onPointsClick={() => setIsPointsModalOpen(true)}
                onHandleNameUpdate={(newName) => setHandleName(newName)}
            />

            <FavoritesModal
                isOpen={isFavoritesModalOpen}
                onClose={() => setIsFavoritesModalOpen(false)}
                onViewStreamer={(id) => {
                    setIsFavoritesModalOpen(false);
                    navigate(`/watch/${id}/before-entry`);
                }}
            />

            <NotificationsModal
                isOpen={isNotificationsModalOpen}
                onClose={handleNotificationsModalClose}
            />
        </div>
    );
}
